import _ from 'underscore';
import 'jquery';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { CampaignType, CampaignTypeByEnumValue, CampaignTypeToStringMap } from '../enums/campaign-type';
import { StatusWaterfallFilterOptions } from '../enums/status-filter-options';
import type { RawWaterfallFilter, WaterfallFilters } from '../types';

const CampaignTypeEnumValuePrefix = 'Enum.CampaignType';
const CampaignStatusEnumValuePrefix = 'Enum.CampaignStatus';
const AdGroupStatusEnumValuePrefix = 'Enum.AdGroupStatus';
const AssetGroupStatusEnumValuePrefix = 'Enum.AssetGroupStatus';

function addPrefixToEnumValue<TPrefix extends `Enum.${string}`, TValue extends string>(
  prefix: TPrefix,
  enumValue: TValue | TValue[]
): `${TPrefix}'${TValue}'` {
  return `${prefix}'${enumValue}'` as `${TPrefix}'${TValue}'`;
}

export function getFilteredCampaignTypeFromRawFilter(rawFilter: RawWaterfallFilter | null | undefined) {
  const filteredCampainTypes = rawFilter
    && rawFilter.value
    && rawFilter.value.query
    && rawFilter.value.query.CampaignType
    && rawFilter.value.query.CampaignType.$in;

  if (_.isEmpty(filteredCampainTypes)) {
    return CampaignType.All;
  }

  return CampaignTypeByEnumValue[filteredCampainTypes[0] as keyof typeof CampaignTypeByEnumValue];
}

export function getFilteredCampaignTypeFromWaterfallFilter(waterfallFilters: WaterfallFilters) {
  const campaignType = _.chain(waterfallFilters).compact().map((waterfallFilter) => {
    if (waterfallFilter.filter && waterfallFilter.filter.key === WaterfallFilterType.CampaignType) {
      return getFilteredCampaignTypeFromRawFilter(waterfallFilter.filter);
    }
    return undefined;
  }).first()
    .value();

  return campaignType;
}

export function getFilteredCampaignStatusFromRawFilter(rawFilter: RawWaterfallFilter | null | undefined) {
  const filteredCampaignStatuses = rawFilter
    && rawFilter.value
    && rawFilter.value.query
    && rawFilter.value.query.CampaignStatus
    && rawFilter.value.query.CampaignStatus.$in;

  if (_.size(filteredCampaignStatuses) === 2
    && filteredCampaignStatuses[0] === addPrefixToEnumValue(CampaignStatusEnumValuePrefix, 'Active')
    && filteredCampaignStatuses[1] === addPrefixToEnumValue(CampaignStatusEnumValuePrefix, 'BudgetPaused')) {
    return StatusWaterfallFilterOptions.AllEnabled;
  }

  return StatusWaterfallFilterOptions.All;
}

export function getFilteredAdGroupStatusFromRawFilter(rawFilter: RawWaterfallFilter | null | undefined) {
  const filteredAdGroupStatuses = rawFilter
    && rawFilter.value
    && rawFilter.value.query
    && rawFilter.value.query.AdGroupStatus
    && rawFilter.value.query.AdGroupStatus.$in;

  if (_.size(filteredAdGroupStatuses) === 2
    && filteredAdGroupStatuses[0] === addPrefixToEnumValue(AdGroupStatusEnumValuePrefix, 'Active')
    && filteredAdGroupStatuses[1] === addPrefixToEnumValue(AdGroupStatusEnumValuePrefix, 'CampaignBudgetPaused')) {
    return StatusWaterfallFilterOptions.AllEnabled;
  }

  return StatusWaterfallFilterOptions.All;
}

export function getFilteredAssetGroupStatusFromRawFilter(rawFilter: RawWaterfallFilter | null | undefined) {
  const filteredAssetGroupStatus = rawFilter
    && rawFilter.value
    && rawFilter.value.query
    && rawFilter.value.query.AssetGroupStatus
    && rawFilter.value.query.AssetGroupStatus.$in;

  if (_.size(filteredAssetGroupStatus) === 1
    && filteredAssetGroupStatus[0] === addPrefixToEnumValue(AssetGroupStatusEnumValuePrefix, 'Active')) {
    return StatusWaterfallFilterOptions.AllEnabled;
  }

  return StatusWaterfallFilterOptions.All;
}

export function convertFilteredCampaignTypeToFilter(campaignType: number): RawWaterfallFilter {
  if (campaignType === CampaignType.All) {
    return { key: WaterfallFilterType.CampaignType, value: { query: null } };
  }

  return {
    key: WaterfallFilterType.CampaignType,
    value: {
      query: {
        CampaignType: {
          $in:
            [addPrefixToEnumValue(
              CampaignTypeEnumValuePrefix,
              [CampaignTypeToStringMap[campaignType]]
            )],
        },
      },
    },
  };
}

export function convertFilteredCampaignStatusToFilter(campaignStatus: number): RawWaterfallFilter {
  if (campaignStatus === StatusWaterfallFilterOptions.All
    || campaignStatus !== StatusWaterfallFilterOptions.AllEnabled) {
    return { key: WaterfallFilterType.CampaignStatus, value: { query: null } };
  }

  return {
    key: WaterfallFilterType.CampaignStatus,
    value: {
      query: {
        CampaignStatus: {
          $in:
            [
              addPrefixToEnumValue(CampaignStatusEnumValuePrefix, 'Active'),
              addPrefixToEnumValue(CampaignStatusEnumValuePrefix, 'BudgetPaused'),
            ],
        },
      },
    },
  };
}

export function convertFilteredAdGroupStatusToFilter(adGroupStatus: number): RawWaterfallFilter {
  if (adGroupStatus === StatusWaterfallFilterOptions.All
    || adGroupStatus !== StatusWaterfallFilterOptions.AllEnabled) {
    return { key: WaterfallFilterType.AdGroupStatus, value: { query: null } };
  }

  return {
    key: WaterfallFilterType.AdGroupStatus,
    value: {
      query: {
        AdGroupStatus: {
          $in:
            [
              addPrefixToEnumValue(AdGroupStatusEnumValuePrefix, 'Active'),
              addPrefixToEnumValue(AdGroupStatusEnumValuePrefix, 'CampaignBudgetPaused'),
            ],
        },
      },
    },
  };
}

export function convertFilteredAssetGroupStatusToFilter(assetGroupStatus: number): RawWaterfallFilter {
  if (assetGroupStatus === StatusWaterfallFilterOptions.All
    || assetGroupStatus !== StatusWaterfallFilterOptions.AllEnabled) {
    return { key: WaterfallFilterType.AssetGroupStatus, value: { query: null } };
  }

  return {
    key: WaterfallFilterType.AssetGroupStatus,
    value: {
      query: {
        AssetGroupStatus: {
          $in:
            [
              addPrefixToEnumValue(AssetGroupStatusEnumValuePrefix, 'Active'),
            ],
        },
      },
    },
  };
}
